import React from 'react';
import { motion } from 'framer-motion';
import { Tilt } from 'react-tilt';

const cardVariants = {
  hidden: { opacity: 0, scale: 0.94, y: 18 },
  visible: (i) => ({
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.06, ease: 'easeOut' },
  }),
  hover: { scale: 1.03 },
};

const iconVariants = {
  initial: { y: 0 },
  animate: { y: [-6, 6, -6], transition: { duration: 2.5, repeat: Infinity, ease: 'easeInOut' } },
  hover: { rotate: 8, scale: 1.12, transition: { duration: 0.25 } },
};

function ServiceCard({ title, desc, icon, tags = [], index = 0, isDark = true }) {
  const shadow = isDark ? '0 12px 24px rgba(0,0,0,0.25)' : '0 18px 30px rgba(2,6,23,0.10)';
  const ring = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)';

  return (
    <Tilt options={{ max: 16, scale: 1.02, speed: 450, glare: true, 'max-glare': 0.15 }}>
      <motion.article
        className="group relative rounded-xl p-[1px]"
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        whileHover="hover"
        custom={index}
        role="article"
        aria-labelledby={`service-title-${index}`}
        tabIndex={0}
        style={{ boxShadow: shadow,
          background:
            'conic-gradient(from 90deg at 50% 50%, rgba(31,165,80,.28), rgba(6,182,212,.28), transparent 65%)'
        }}
      >
        {/* Card body */}
        <div
          className="relative rounded-xl h-full ring-1 p-5 sm:p-6 backdrop-blur"
          style={{ background: 'var(--card)', borderColor: ring }}
        >
          <motion.div
            className="flex items-center justify-center w-16 h-16 rounded-2xl text-white text-3xl mb-4 mx-auto shadow-md"
            variants={iconVariants}
            initial="initial"
            animate="animate"
            whileHover="hover"
            aria-hidden="true"
            style={{
              background: 'linear-gradient(135deg, var(--brand,#1FA550), var(--accent,#06B6D4))',
              boxShadow: shadow,
            }}
          >
            {icon}
          </motion.div>

          <h3 id={`service-title-${index}`} className="text-xl font-semibold mb-2 text-center">
            {title}
          </h3>

          <p className="text-sm mb-4 leading-relaxed text-center" style={{ color: 'var(--muted)' }}>
            {desc}
          </p>

          {tags.length > 0 && (
            <ul className="mt-2 mb-4 flex flex-wrap gap-2 justify-center">
              {tags.map((t) => (
                <li
                  key={t}
                  className="text-xs rounded-full px-2.5 py-1 ring-1"
                  style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(2,6,23,0.04)', borderColor: ring, color: 'var(--muted)' }}
                >
                  {t}
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-center justify-between">
            <a
              href="/contact"
              className="inline-flex items-center gap-2 text-sm font-semibold hover:underline"
              style={{ color: 'var(--brand, #1FA550)' }}
              aria-label={`Learn more about ${title}`}
            >
              Learn more
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M5 12h14m0 0-6-6m6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-3 py-2 rounded-lg ring-1"
              style={{ background: isDark ? 'rgba(255,255,255,0.06)' : '#fff', borderColor: ring, color: 'var(--text)' }}
            >
              Add to plan
            </motion.button>
          </div>
        </div>
      </motion.article>
    </Tilt>
  );
}

export default ServiceCard;
